import { cva, type VariantProps } from 'class-variance-authority';
import { Tabs as TabsPrimitive } from 'radix-ui';
import * as React from 'react';

import { mergeClasses } from '@/utils';

const TABS_LIST_VARIANTS = cva(
  'rounded-lg p-1 data-[variant=line]:rounded-none group/tabs-list inline-flex w-fit items-center justify-center group-data-[orientation=vertical]/tabs:h-fit group-data-[orientation=vertical]/tabs:flex-col',
  {
    variants: {
      variant: {
        default: 'bg-primary-base',
        line: 'gap-1 bg-transparent',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

type IndicatorStyle = { left: number; width: number };

const Tabs = ({
  className,
  orientation = 'horizontal',
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) => {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      className={mergeClasses(
        'gap-2 group/tabs flex data-[orientation=horizontal]:flex-col',
        className,
      )}
      {...props}
    />
  );
};

const TabsList = ({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & VariantProps<typeof TABS_LIST_VARIANTS>) => {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      className={mergeClasses(TABS_LIST_VARIANTS({ variant }), className)}
      {...props}
    />
  );
};

const TabsTrigger = ({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) => {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={mergeClasses(
        "gap-1.5 rounded-md border border-transparent px-1.5 py-0.5 text-body-sm font-medium group-data-[variant=default]/tabs-list:data-[state=active]:shadow-sm group-data-[variant=line]/tabs-list:data-[state=active]:shadow-none [&_svg:not([class*='size-'])]:size-4 relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center whitespace-nowrap text-neutral-0/60 transition-all group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start hover:text-neutral-0 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        'group-data-[variant=line]/tabs-list:text-primary-base/60 group-data-[variant=line]/tabs-list:hover:text-primary-base group-data-[variant=line]/tabs-list:data-[state=active]:text-primary-base group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-[state=active]:bg-transparent',
        'data-[state=active]:bg-primary-darker data-[state=active]:text-neutral-0',
        'after:absolute after:bg-primary-base after:opacity-0 after:transition-opacity group-data-[orientation=horizontal]/tabs:after:inset-x-0 group-data-[orientation=horizontal]/tabs:after:bottom-[-5px] group-data-[orientation=horizontal]/tabs:after:h-0.5 group-data-[orientation=vertical]/tabs:after:inset-y-0 group-data-[orientation=vertical]/tabs:after:-right-1 group-data-[orientation=vertical]/tabs:after:w-0.5 group-data-[variant=line]/tabs-list:data-[state=active]:after:opacity-100',
        className,
      )}
      {...props}
    />
  );
};

const TabsContent = ({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) => {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={mergeClasses('text-body-sm flex-1 outline-none', className)}
      {...props}
    />
  );
};

const InlineTabs = ({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) => {
  return (
    <TabsPrimitive.Root
      data-slot="inline-tabs"
      className={mergeClasses('flex flex-col gap-3', className)}
      {...props}
    />
  );
};

const InlineTabsList = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) => {
  const listRef = React.useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = React.useState<IndicatorStyle>({ left: 0, width: 0 });

  const updateIndicator = React.useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    const activeTab = list.querySelector<HTMLElement>('[data-state="active"]');
    if (activeTab) {
      setIndicator({
        left: activeTab.offsetLeft,
        width: activeTab.offsetWidth,
      });
    }
  }, []);

  React.useEffect(() => {
    updateIndicator();

    const list = listRef.current;
    if (!list) return;

    const observer = new MutationObserver(updateIndicator);
    observer.observe(list, {
      attributes: true,
      subtree: true,
      attributeFilter: ['data-state'],
    });

    return () => observer.disconnect();
  }, [updateIndicator]);

  return (
    <TabsPrimitive.List
      ref={listRef}
      data-slot="inline-tabs-list"
      className={mergeClasses(
        'relative flex flex-row gap-10 border-b-2 border-neutral-4',
        className,
      )}
      {...props}
    >
      <div
        className="absolute bottom-[-2px] h-0.5 bg-primary-base transition-all duration-300 ease-in-out"
        style={{ left: indicator.left, width: indicator.width }}
      />
      {children}
    </TabsPrimitive.List>
  );
};

const InlineTabsTrigger = ({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) => {
  return (
    <TabsPrimitive.Trigger
      data-slot="inline-tabs-trigger"
      className={mergeClasses(
        'py-2 text-body-sm font-medium text-neutral-7 transition-colors text-center',
        'hover:text-neutral-9 data-[state=active]:text-neutral-9 data-[state=active]:font-semibold',
        className,
      )}
      {...props}
    />
  );
};

export { Tabs, TabsContent, TabsList, TabsTrigger, InlineTabs, InlineTabsList, InlineTabsTrigger };
